import React, { useState } from 'react';
import { Plus, X, MessageSquare, Send } from 'lucide-react';
import { formatBrasilia } from '../../../shared/lib/date';
import { FeedbackForm } from '../components/FeedbackForm';
import { FeedbackDetailModal } from '../components/FeedbackDetailModal';
import { useFeedbacks } from '../hooks';
import { publishFeedback } from '../api';
import type { Feedback } from '../../../shared/lib/api';
import { useQueryClient } from '@tanstack/react-query';
import { FEEDBACKS_KEYS } from '../api';

const LIFECYCLE_LABELS: Record<string, string> = {
  draft: 'Rascunho',
  published: 'Publicado',
  released: 'Liberado',
};

const LIFECYCLE_COLORS: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  published: 'bg-yellow-100 text-yellow-700',
  released: 'bg-green-100 text-green-700',
};

const STATUS_LABELS: Record<string, string> = {
  rascunho: 'Rascunho',
  enviado_governanca: 'Enviado à Governança',
  validado: 'Validado',
  devolvido_ajustes: 'Devolvido para Ajustes',
};

const STATUS_COLORS: Record<string, string> = {
  rascunho: 'bg-gray-100 text-gray-700',
  enviado_governanca: 'bg-yellow-100 text-yellow-700',
  validado: 'bg-green-100 text-green-700',
  devolvido_ajustes: 'bg-red-100 text-red-700',
};

const FeedbackPage: React.FC = () => {
  const [showForm, setShowForm] = useState(false);
  const [editDraft, setEditDraft] = useState<Feedback | null>(null);
  const [detailFeedback, setDetailFeedback] = useState<Feedback | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const { data, isLoading, isError, error } = useFeedbacks();

  const handlePublish = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await publishFeedback(id);
      queryClient.invalidateQueries({ queryKey: FEEDBACKS_KEYS.all });
      setSuccessMessage('Feedback publicado com sucesso!');
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch {
      setSuccessMessage('Erro ao publicar feedback.');
    }
  };
  const feedbacks = data?.data ?? [];

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditDraft(null);
    queryClient.invalidateQueries({ queryKey: FEEDBACKS_KEYS.all });
    setSuccessMessage('Feedback salvo com sucesso!');
    setTimeout(() => setSuccessMessage(null), 5000);
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Feedbacks</h1>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-5 h-5 mr-2" />
          Novo Feedback
        </button>
      </div>

      {successMessage && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-sm text-green-600">{successMessage}</p>
        </div>
      )}

      {isLoading ? (
        <div>Carregando...</div>
      ) : isError ? (
        <div className="text-red-600">Erro: {(error as Error)?.message}</div>
      ) : feedbacks.length === 0 ? (
        <div className="p-8 text-center bg-gray-50 rounded-xl border border-gray-200">
          <MessageSquare className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500">Nenhum feedback registrado ainda.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Colaborador</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Período</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Data/Hora</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {feedbacks.map(fb => (
                <tr
                  key={fb.id}
                  className="hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => {
                    if (fb.lifecycleStatus === 'draft') {
                      setEditDraft(fb);
                      setShowForm(true);
                    } else {
                      setDetailFeedback(fb);
                    }
                  }}
                >
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {fb.collaborator?.name ?? fb.collaboratorId}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{fb.referencePeriod}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatBrasilia(fb.createdAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${LIFECYCLE_COLORS[fb.lifecycleStatus] ?? 'bg-gray-100 text-gray-700'}`}>
                      {LIFECYCLE_LABELS[fb.lifecycleStatus] ?? fb.lifecycleStatus}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" onClick={e => e.stopPropagation()}>
                    {fb.lifecycleStatus === 'draft' && (
                      <button
                        onClick={(e) => handlePublish(fb.id, e)}
                        className="flex items-center gap-1 px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                      >
                        <Send className="w-3 h-3" /> Publicar
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b sticky top-0 bg-white z-10">
              <h2 className="text-xl font-bold text-gray-900">Novo Feedback</h2>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6">
              <FeedbackForm
                onSuccess={handleFormSuccess}
                onCancel={() => { setShowForm(false); setEditDraft(null); }}
                initialDraft={editDraft ?? undefined}
              />
            </div>
          </div>
        </div>
      )}

      {detailFeedback && (
        <FeedbackDetailModal feedback={detailFeedback} onClose={() => setDetailFeedback(null)} />
      )}
    </div>
  );
};

export default FeedbackPage;
